import { AxiosResponse } from "axios";
import qs from "qs";
import { IOptions } from "../../api-sdk";
import BaseService from "../baseService";
import {
  ILikeOrDislikeInfoPayload,
  ILikeOrDislikeInfoResponse,
  ILikeOrDislikePayload,
  ILikeOrDislikeResponse,
  ILikeOrDislikeStatusPayload,
  ILikeOrDislikeStatusResponse,
} from "./like.or.dislike.types";
import { filterNonNull } from "../../../utils";

class LikeOrDislikeService extends BaseService {
  constructor(options: IOptions) {
    super(options);
  }

  /**
   * Send a like request for the provided target.
   *
   * @param payload Payload containing the target `id`, `type`, and optional `target`.
   * @returns Axios response containing the service message.
   */
  async like(
    payload: ILikeOrDislikePayload
  ): Promise<AxiosResponse<ILikeOrDislikeResponse>> {
    return this.request({
      method: "POST",
      url: "/api/v1/like-dislike",
      data: payload,
    });
  }

  /**
   * Send a dislike request for the provided target.
   *
   * @param payload Payload containing the target `id`, `type`, and optional `target`.
   * @returns Axios response containing the service message.
   */
  async dislike(
    payload: ILikeOrDislikePayload
  ): Promise<AxiosResponse<ILikeOrDislikeResponse>> {
    return this.request({
      method: "POST",
      url: "/api/v1/like-dislike",
      data: payload,
    });
  }

  /**
   * Send an unlike request for the provided target.
   *
   * @param payload Payload containing the target `id`, `type`, and optional `target`.
   * @returns Axios response containing the service message.
   */
  async unlike(
    payload: ILikeOrDislikePayload
  ): Promise<AxiosResponse<ILikeOrDislikeResponse>> {
    return this.request({
      method: "POST",
      url: "/api/v1/like-dislike",
      data: payload,
    });
  }

  /**
   * Send an undislike request for the provided target.
   *
   * @param payload Payload containing the target `id`, `type`, and optional `target`.
   * @returns Axios response containing the service message.
   */
  async undislike(
    payload: ILikeOrDislikePayload
  ): Promise<AxiosResponse<ILikeOrDislikeResponse>> {
    return this.request({
      method: "POST",
      url: "/api/v1/like-dislike",
      data: payload,
    });
  }

  /**
   * Fetch the current reaction status for multiple targets (preferred).
   *
   * @param payload Payload containing up to 20 target ids.
   * @returns Axios response with reaction states for each requested target id.
   */
  async getInfo(
    payload: ILikeOrDislikeInfoPayload
  ): Promise<AxiosResponse<ILikeOrDislikeInfoResponse>> {
    return this.request({
      method: "GET",
      url: `/api/v1/like-dislike/info?${qs.stringify(
        filterNonNull(payload || {})
      )}`,
    });
  }

  /**
   * @deprecated The single-target status endpoint is deprecated. Use `getInfo`
   * instead to retrieve reaction state.
   *
   * @param payload Payload containing the target `id`.
   * @returns Axios response with `isLiked` and `isDisliked` flags.
   */
  async getStatus(
    payload: ILikeOrDislikeStatusPayload
  ): Promise<AxiosResponse<ILikeOrDislikeStatusResponse>> {
    return this.request({
      method: "GET",
      url: `/api/v1/like-dislike/status/${payload.id}`,
    });
  }
}

export default LikeOrDislikeService;
